import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { ChatReplyRequest, CreatePrivateChatRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useAddPrivateChat = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: CreatePrivateChatRequest) => api.postPortfolioChatsPrivateV2(data).then((x) => x.data),
    onSuccess: (_, payload) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_USER_CHAT_DETAILS(payload.userId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_CHATS_LIST() });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.user-detail.chat.send-error") });
    },
  });
};

const useDeleteChat = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (chatId: string) => api.deletePortfolioChatsByIdV2(chatId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_CHATS_LIST() });
      showFlashToast({ variant: "success", message: t("page.portfolio.chats.notification.delete.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.portfolio.chats.notification.delete.error") });
    },
  });
};

const useLeaveGroupChat = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (chatId: string) => api.postPortfolioChatsMembersLeaveV2(chatId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_CHATS_LIST() });
      showFlashToast({ variant: "success", message: t("page.portfolio.chats.leave.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.portfolio.chats.leave.error") });
    },
  });
};

const useBlockUser = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) => api.postPortfolioChatsBlocksV2(userId),
    onSuccess: (_, userId) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_USER_CHAT_DETAILS(userId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_CHATS_LIST() });

      showFlashToast({ variant: "success", message: t("page.portfolio.chats.notification.block.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.portfolio.chats.notification.block.error") });
    },
  });
};

const useUnblockUser = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (userId: string) => api.deletePortfolioChatsBlocksByIdV2(userId),
    onSuccess: (_, userId) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_USER_CHAT_DETAILS(userId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_CHATS_LIST() });

      showFlashToast({ variant: "success", message: t("page.portfolio.chats.unblock.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("page.portfolio.chats.unblock.error") });
    },
  });
};

const useAddChatReply = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ chatId, data }: { chatId: string; data: ChatReplyRequest }) =>
      api.postPortfolioChatsReplyV2(chatId, data).then((x) => x.data),
    onSuccess: async (_, payload) => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PORTFOLIO_CHATS_REPLIES(payload.chatId) });
    },
    onError() {
      showFlashToast({ variant: "alert", message: t("page.user-detail.chat.send-error") });
    },
  });
};

export const portfolioChatMutations = {
  useAddPrivateChat,
  useAddChatReply,
  useDeleteChat,
  useLeaveGroupChat,
  useBlockUser,
  useUnblockUser,
};
